// messageCache — learner-side IDB cache of message metadata.
//
// Why: cold offline launch (PWA opened on a hill with no signal) takes
// ~5s for Firestore listeners to time out before reporting empty. We
// persist the last-seen messages so the UI paints something instantly
// while the live listener catches up.
//
// What's stored: MessageDoc + docId. No blobs (messages are text only).
// Timestamps serialised to ms; rehydrated to Timestamp on read.
//
// Stage 10: refactored to use createAudienceCache factory. External API
// (writeMessagesCache / readMessagesCache / deleteMessageFromCache) is
// unchanged so all callers remain green — see audienceCache.ts.

import type { MessageDoc, WithId } from "@goodbye/firebase";
import { Timestamp } from "firebase/firestore";
import { createAudienceCache } from "./audienceCache";

interface StoredMessage {
  id: string;
  createdAtMs: number;
  retractedAtMs: number | null;
  body: Omit<MessageDoc, "createdAt" | "retractedAt">;
}

function toStored(m: WithId<MessageDoc>): StoredMessage {
  const { id, createdAt, retractedAt, ...rest } = m;
  return {
    id,
    createdAtMs: createdAt instanceof Timestamp ? createdAt.toMillis() : 0,
    retractedAtMs:
      retractedAt instanceof Timestamp ? retractedAt.toMillis() : null,
    body: rest,
  };
}

function fromStored(s: StoredMessage): WithId<MessageDoc> {
  return {
    ...s.body,
    id: s.id,
    createdAt: Timestamp.fromMillis(s.createdAtMs),
    retractedAt:
      s.retractedAtMs != null
        ? Timestamp.fromMillis(s.retractedAtMs)
        : undefined,
  } as WithId<MessageDoc>;
}

const cache = createAudienceCache<WithId<MessageDoc>, StoredMessage>({
  dbName: "goodbye-user-messages",
  storeName: "messages",
  encode: toStored,
  decode: fromStored,
});

export async function writeMessagesCache(
  messages: WithId<MessageDoc>[],
): Promise<void> {
  await cache.write(messages);
}

export async function readMessagesCache(): Promise<WithId<MessageDoc>[]> {
  return cache.read();
}

/** Called when a silent message_retracted push arrives. */
export async function deleteMessageFromCache(
  messageId: string,
): Promise<void> {
  await cache.delete(messageId);
}
